import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { Download, FileText, Loader2, AlertCircle, AlertTriangle, ExternalLink } from 'lucide-react'

interface CategorySummary {
  count: number
  ventas: number
  rectificativas: number
  base: number
  iva: number
  rec: number
  ret: number
  total: number
}

interface ToReviewItem {
  tipfac: string
  codfac: number
  fecha: string
  cliente: string
  clifac: number
  nif: string
  base: number
  iva: number
  total: number
  severity: 'high' | 'medium'
  reason: string
}

interface SummaryResponse {
  year: number
  month: number
  month_name: string
  categories: Record<string, CategorySummary>
  to_review: ToReviewItem[]
}

const MONTHS = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
]

const CATEGORY_ORDER = ['con iva', 'sin iva', 'intracomunitarias', 'exportaciones']

const CATEGORY_DESC: Record<string, string> = {
  'con iva': 'Ventas nacionales con IVA (TIVFAC=0)',
  'sin iva': 'Indemnizaciones / facturas sin IVA (TIVFAC=1)',
  'intracomunitarias': 'Operaciones intracomunitarias UE (TIVFAC=2)',
  'exportaciones': 'Exportaciones / Canarias (TIVFAC=3)',
}

function fmtEur(n: number): string {
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
}

export default function AsesoriaExportPage() {
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth())
  const [committedParams, setCommittedParams] = useState<{ year: number; month: number } | null>(null)

  const { data, isLoading, error } = useQuery<SummaryResponse>({
    queryKey: ['asesoria-summary', committedParams?.year, committedParams?.month],
    queryFn: async () => {
      const params = { year: committedParams!.year, month: committedParams!.month }
      return (await axios.get<SummaryResponse>('/api/reports/asesoria/summary', { params })).data
    },
    enabled: !!committedParams,
  })

  const handleLoad = () => setCommittedParams({ year, month })

  const handleDownload = () => {
    if (!committedParams) return
    const url = `/api/reports/asesoria/csv?year=${committedParams.year}&month=${committedParams.month}`
    window.location.href = url
  }

  const totalGlobal = data
    ? Object.values(data.categories).reduce((s, c) => s + c.total, 0)
    : 0

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <FileText className="w-7 h-7" />
          Exportación facturas asesoría
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Genera los 4 CSVs mensuales (con iva, sin iva, intracomunitarias, exportaciones)
          recalculando BASE e IVA desde el total cobrado para garantizar cuadre
          BAS+IVA+REC-RET=TOT al céntimo en todas las facturas.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-5 mb-5">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Año</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              {Array.from({ length: 6 }, (_, i) => now.getFullYear() - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Mes</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleLoad}
            disabled={isLoading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Calcular resumen
          </button>
          {data && (
            <button
              onClick={handleDownload}
              className="ml-auto bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Descargar ZIP ({data.month_name} {data.year})
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded mb-4 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>Error cargando datos: {String((error as Error).message || error)}</span>
        </div>
      )}

      {data && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-5 py-3 bg-gray-50 border-b">
            <h2 className="text-sm font-semibold text-gray-700">
              Resumen {data.month_name} {data.year}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Total cobrado en el mes: <span className="font-semibold text-gray-800">{fmtEur(totalGlobal)}</span>
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Categoría</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Nº facturas</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Ventas</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Rectif.</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Base</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">IVA</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Rec.</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Ret.</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Total</th>
              </tr>
            </thead>
            <tbody>
              {CATEGORY_ORDER.map((cat) => {
                const s = data.categories[cat]
                if (!s) return null
                const empty = s.count === 0
                return (
                  <tr key={cat} className={empty ? 'text-gray-400' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-2">
                      <div className="font-medium">{cat}</div>
                      <div className="text-xs text-gray-500">{CATEGORY_DESC[cat]}</div>
                    </td>
                    <td className="text-right px-3 py-2 font-mono">{s.count}</td>
                    <td className="text-right px-3 py-2 font-mono">{s.ventas}</td>
                    <td className="text-right px-3 py-2 font-mono">{s.rectificativas}</td>
                    <td className="text-right px-3 py-2 font-mono">{fmtEur(s.base)}</td>
                    <td className="text-right px-3 py-2 font-mono">{fmtEur(s.iva)}</td>
                    <td className="text-right px-3 py-2 font-mono">{fmtEur(s.rec)}</td>
                    <td className="text-right px-3 py-2 font-mono">{fmtEur(s.ret)}</td>
                    <td className="text-right px-3 py-2 font-mono font-semibold">{fmtEur(s.total)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && data.to_review.length > 0 && (
        <div className="mt-5 bg-amber-50 border border-amber-300 rounded-lg overflow-hidden">
          <div className="px-5 py-3 bg-amber-100 border-b border-amber-300">
            <h2 className="text-sm font-semibold text-amber-900 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Facturas a revisar antes de enviar a la asesoría ({data.to_review.length})
            </h2>
            <p className="text-xs text-amber-800 mt-1">
              Posibles intracomunitarias mal marcadas en Factusol (TIVFAC=0).
              Verifica el VAT en VIES y, si corresponde, corrige TIVFAC a 2 en la cabecera
              de la factura y la ficha del cliente antes de regenerar el ZIP.
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-amber-50 border-b border-amber-200">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-amber-900">Gravedad</th>
                <th className="text-left px-3 py-2 font-medium text-amber-900">Factura</th>
                <th className="text-left px-3 py-2 font-medium text-amber-900">Fecha</th>
                <th className="text-left px-3 py-2 font-medium text-amber-900">Cliente</th>
                <th className="text-left px-3 py-2 font-medium text-amber-900">NIF/VAT</th>
                <th className="text-right px-3 py-2 font-medium text-amber-900">Base</th>
                <th className="text-right px-3 py-2 font-medium text-amber-900">IVA</th>
                <th className="text-right px-3 py-2 font-medium text-amber-900">Total</th>
                <th className="text-left px-3 py-2 font-medium text-amber-900">Motivo</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.to_review.map((r) => {
                const vatCountry = r.nif.slice(0, 2).toUpperCase()
                const vatNumber = r.nif.slice(2)
                const viesUrl = `https://ec.europa.eu/taxation_customs/vies/vatResponse.html?memberStateCode=${vatCountry}&number=${vatNumber}&traderName=&traderStreet=&traderPostalCode=&traderCity=&traderCompanyType=&action=check`
                return (
                  <tr key={`${r.tipfac}-${r.codfac}`} className="hover:bg-amber-100/30 border-b border-amber-100">
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${r.severity === 'high' ? 'bg-red-100 text-red-800' : 'bg-amber-200 text-amber-900'}`}>
                        {r.severity === 'high' ? 'alta' : 'media'}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono">{r.tipfac}-{r.codfac}</td>
                    <td className="px-3 py-2">{r.fecha}</td>
                    <td className="px-3 py-2">{r.cliente}</td>
                    <td className="px-3 py-2 font-mono">{r.nif}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtEur(r.base)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtEur(r.iva)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{fmtEur(r.total)}</td>
                    <td className="px-3 py-2 text-xs text-amber-900">{r.reason}</td>
                    <td className="px-3 py-2">
                      <a href={viesUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs inline-flex items-center gap-1">
                        VIES <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {!data && !isLoading && !error && (
        <div className="text-center py-12 text-gray-500 text-sm">
          Selecciona un mes y pulsa "Calcular resumen" para ver los datos antes de descargar.
        </div>
      )}
    </div>
  )
}
